/**
 * BlinkSkillEffect — Astral tree foundation active: instant short-range teleport.
 *
 * Pure repositioning. No windup, no damage, no stun, no invulnerability window.
 * The player is simply somewhere else. Cooldown enforces spacing between blinks.
 *
 * Upgrade hooks (wired here, values resolved by upgrade tasks):
 * - blink_reach: `rangeMultiplier` (multiplicative) — extends blink range by 1.3×.
 * - blink_cooldown: `cooldownReduction` (additive, fraction) — reduces cooldown by 20%.
 *
 * Blink is superseded by Phase Step (phase_step) at XP threshold 200.
 * When Phase Step unlocks, SkillProgressionComponent replaces BlinkSkillEffect
 * in SkillActivationComponent's slot. The resolver continues to route
 * blink_reach upgrades through the inherited upgrade chain.
 *
 * XP is awarded via AstralTeleportSystem.onTeleport — the XP hook registers at
 * construction and fires after every successful executeTeleport() call.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see astral-spec.md § blink
 * @see design-decisions.md § Astral Base Values (base range 4 tiles)
 * @see ADR-064 (attunement shared systems architecture)
 */

import type { SkillEffect } from '../../SkillEffect.js';
import type { AstralTeleportSystem, TeleportEvent } from '../../../systems/AstralTeleportSystem.js';
import { TILE_SIZE } from '../../../utils/Constants.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface BlinkDeps {
  readonly resolver: {
    getMultiplicativeEffect(skillId: string, key: string): number;
    getAdditiveEffect(skillId: string, key: string): number;
  };
  readonly teleport: Pick<
    AstralTeleportSystem,
    'executeTeleport' | 'onTeleport'
  >;
  readonly player: {
    getPosition(): { x: number; y: number };
  };
  readonly targeting: {
    /** Cursor / intended destination in world coordinates. */
    getTargetPosition(): { x: number; y: number };
  };
  readonly xpSource?: {
    /** Called after a successful teleport. dangerPresent drives the XP bonus. */
    awardTeleportXp(dangerPresent: boolean): void;
    isDangerPresent(): boolean;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface BlinkConfig {
  readonly cooldownMs: number;
  readonly baseRangeTiles: number;
}

const DEFAULT_CONFIG: BlinkConfig = {
  cooldownMs: 400,      // fast cooldown tier
  baseRangeTiles: 4,    // from design-decisions.md § Astral Base Values
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class BlinkSkillEffect implements SkillEffect {
  readonly skillId = 'blink';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: BlinkDeps;
  private readonly config: BlinkConfig;

  private _cooldownRemaining = 0;
  private _teleportsFired = 0;
  private _resolvedCooldownMs = 0;
  private _resolvedRangeTiles = 0;
  private _lastTeleportFrom: { x: number; y: number } | null = null;
  private _lastTeleportTo: { x: number; y: number } | null = null;

  constructor(deps: BlinkDeps, config: Partial<BlinkConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };

    // Wire XP hook if xpSource is provided.
    if (deps.xpSource) {
      const xp = deps.xpSource;
      deps.teleport.onTeleport((_event: TeleportEvent) => {
        xp.awardTeleportXp(xp.isDangerPresent());
      });
    }
  }

  // --- State getters ---

  get teleportsFired(): number { return this._teleportsFired; }
  get resolvedCooldownMs(): number { return this._resolvedCooldownMs; }
  get resolvedRangeTiles(): number { return this._resolvedRangeTiles; }
  get lastTeleportFrom(): Readonly<{ x: number; y: number }> | null { return this._lastTeleportFrom; }
  get lastTeleportTo(): Readonly<{ x: number; y: number }> | null { return this._lastTeleportTo; }
  get cooldown(): number { return this._cooldownRemaining; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Resolve range (base × rangeMultiplier from blink_reach).
    const rangeMultiplier = this.deps.resolver.getMultiplicativeEffect('blink', 'rangeMultiplier');
    this._resolvedRangeTiles = this.config.baseRangeTiles * rangeMultiplier;
    const rangePx = this._resolvedRangeTiles * TILE_SIZE;

    // Resolve cooldown (base × (1 - cooldownReduction) from blink_cooldown).
    const cooldownReduction = this.deps.resolver.getAdditiveEffect('blink', 'cooldownReduction');
    this._resolvedCooldownMs = Math.max(100, this.config.cooldownMs * (1 - cooldownReduction));

    // Compute destination: clamp cursor to max range.
    const from = this.deps.player.getPosition();
    const target = this.deps.targeting.getTargetPosition();

    const dx = target.x - from.x;
    const dy = target.y - from.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    let toX: number;
    let toY: number;

    if (dist <= 0.001) {
      // Cursor is exactly on player — no movement.
      toX = from.x;
      toY = from.y;
    } else if (dist <= rangePx) {
      toX = target.x;
      toY = target.y;
    } else {
      // Clamp to max range in the target direction.
      toX = from.x + (dx / dist) * rangePx;
      toY = from.y + (dy / dist) * rangePx;
    }

    this._lastTeleportFrom = { x: from.x, y: from.y };
    this._lastTeleportTo = { x: toX, y: toY };

    // Execute teleport (no invuln — that's Phase Step's territory).
    this.deps.teleport.executeTeleport(from.x, from.y, toX, toY);
    this._teleportsFired++;

    // Start cooldown.
    this._cooldownRemaining = this._resolvedCooldownMs;

    return true;
  }

  deactivate(): void {
    // Instant — no toggle.
  }

  isActive(): boolean {
    return false; // instant skill
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }
}
